import { Card, CardContent, CardHeader, Input, ScrollArea } from '@ibsys/kit';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  KM_EVAL_TARGET_PARAM,
  useDeleteKmAssessment,
  useKmAssessment,
  type KmAssessmentResponse,
} from '@/entities/expert-report-km';
import { toPredicateValue } from '@/entities/predicate';
import { useHasPermission } from '@/entities/profile';
import { toastWarning, useNavigationTarget } from '@/shared/lib';
import { ConfirmationButton, SaveButton } from '@/shared/ui';
import { BlockPointsField } from '../BlockCard/BlockPointsField';
import { KmContextSection } from '../KmContextSection/KmContextSection';
import { KmEvalList } from './KmEvalList';
import { useSaveKmAssessment } from './useSaveKmAssessment';
import type { KmAssessmentFormValues } from './types';

type KmAssessmentCardProps = {
  assessmentId: number;
  onDeleted: () => void;
  className?: string;
};

const mapAssessmentToForm = (assessment: KmAssessmentResponse): KmAssessmentFormValues => ({
  name: assessment.name,
  evals: [...assessment.evals]
    .sort((a, b) => a.orderPosition - b.orderPosition)
    .map((item) => ({
      id: item.id,
      name: item.name,
      text: item.text,
      predicate: toPredicateValue(item.predicate),
      noteSets: item.noteSets ?? [],
    })),
});

/**
 * Карточка единицы оценивания заключения на модели знаний: название, шкала и определения
 * оценки с их условиями применимости и наборами знаний. Шкалу единицы определяет система по её определениям, вручную
 * она не задаётся.
 */
export const KmAssessmentCard: FC<KmAssessmentCardProps> = ({ assessmentId, onDeleted, className }) => {
  const { t } = useTranslation(['common', 'expertReport']);
  const { data: assessment, isLoading } = useKmAssessment(assessmentId);
  const { mutateAsync: deleteAssessment, isPending: isDeleting } = useDeleteKmAssessment();
  const saveAssessment = useSaveKmAssessment();
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const evalTarget = useNavigationTarget(KM_EVAL_TARGET_PARAM, viewportRef);
  const [isSaving, setIsSaving] = useState(false);
  // В прошлом сохранении что-то не удалось (шкала или отдельная правка) — даём повторить попытку без правок
  const [hasSaveFailure, setHasSaveFailure] = useState(false);

  const defaultValues = useMemo<KmAssessmentFormValues>(
    () => (assessment ? mapAssessmentToForm(assessment) : { name: '', evals: [] }),
    [assessment],
  );

  const form = useForm<KmAssessmentFormValues>({ defaultValues });
  const { control, reset, handleSubmit, formState } = form;

  // Состояние единицы, которым форма заполнена в последний раз
  const appliedFormRef = useRef<string | null>(null);

  // Форма пересобирается, только когда изменилось то, что она правит: назначение контекста оценки
  // тоже обновляет единицу, но стирать несохранённые правки карточки не должно
  useEffect(() => {
    if (!assessment) return;
    const values = mapAssessmentToForm(assessment);
    const snapshot = JSON.stringify(values);
    if (snapshot === appliedFormRef.current) return;
    appliedFormRef.current = snapshot;
    reset(values);
  }, [assessment, reset]);

  const focusNameInput = useCallback(() => {
    nameInputRef.current?.focus();
    nameInputRef.current?.select();
  }, []);

  // При переходе к другой единице фокус переносим на поле названия, а неудачу расчёта
  // прошлой единицы забываем — она относится только к ней
  useEffect(() => {
    setHasSaveFailure(false);
    // Переход к определению оценки открывает единицу ради него самого: курсор в названии
    // единицы увёл бы внимание с прочитанного определения
    if (evalTarget.targetKey) return;
    const timer = setTimeout(focusNameInput, 100);
    return () => clearTimeout(timer);
  }, [assessmentId, focusNameInput, evalTarget.targetKey]);

  const onSubmit = async (values: KmAssessmentFormValues) => {
    if (!assessment) return;
    setIsSaving(true);
    try {
      const { pointsError, hasRejectedChanges } = await saveAssessment(assessment, values);
      // Сохранение остаётся в силе, даже если шкалу определить не удалось, — предупреждаем отдельно
      setHasSaveFailure(Boolean(pointsError) || hasRejectedChanges);
      if (pointsError) {
        toastWarning(t('expertReport:km.pointsResolveFailed'), { description: pointsError });
      }
      // Причину отказа показал общий обработчик ошибок — здесь остаётся сказать, что сохранено не всё
      if (hasRejectedChanges) {
        toastWarning(t('expertReport:km.partialSaveFailed'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    await deleteAssessment(assessmentId);
    onDeleted();
  };

  if (isLoading) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('common:loading')}</div>
        </CardHeader>
      </Card>
    );
  }

  if (!assessment) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('expertReport:selectItem')}</div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <FormProvider {...form}>
      <Card className={className}>
        <CardHeader className="space-y-4">
          <Input
            ref={nameInputRef}
            control={control}
            name="name"
            label={t('expertReport:km.assessmentName')}
            disabled={!allowAdmin}
            rules={{
              required: { value: true, message: t('common:required') },
              // Имя из одних пробелов required не отсекает, а сохранять единицу с таким именем нельзя
              validate: (value) => (typeof value === 'string' && value.trim().length > 0) || t('common:required'),
            }}
          />

          <BlockPointsField
            maxPoints={assessment.maxPoints}
            possiblePoints={assessment.possiblePoints}
            possibleValues={assessment.possibleValues}
            isProcessing={isSaving}
          />

          {allowAdmin && (
            <SaveButton
              className="justify-end"
              isDirty={formState.isDirty}
              allowSaveWithoutChanges={hasSaveFailure}
              isLoading={isSaving}
              onClick={handleSubmit(onSubmit)}
              secondaryAction={
                <ConfirmationButton
                  onConfirm={handleDelete}
                  isLoadingConfirm={isDeleting}
                  title={t('expertReport:km.removeAssessment')}
                  description={
                    <Trans
                      i18nKey="expertReport:km.removeAssessmentDescription"
                      values={{ assessmentName: assessment.name }}
                      components={{ strong: <strong /> }}
                    />
                  }
                  trigger={{
                    variant: 'outline',
                    color: 'destructive',
                    children: t('expertReport:km.deleteAssessment'),
                  }}
                />
              }
            />
          )}
        </CardHeader>

        <CardContent className="space-y-4 min-h-0 overflow-hidden">
          <ScrollArea className="h-full" viewPortProps={{ ref: viewportRef }}>
            <div className="flex flex-col gap-6">
              <KmEvalList isUpdating={isSaving} evalTarget={evalTarget} />
              <KmContextSection reportId={assessment.reportId} assessmentId={assessmentId} />
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </FormProvider>
  );
};
